import React, { useState } from 'react'
import {
  Box,
  Typography,
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import useFeatureFlags from '../../../../../hooks/setting/useFeatureFlags'

// MF-528 — one flag row: title + optional description + on/off switch + optional delete.
const FlagRow = ({ title, description, value, disabled, onToggle, onDelete }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      py: 1.25,
      borderBottom: '1px solid #eef2f7'
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#24303f' }}>{title}</Typography>
      {description ? (
        <Typography sx={{ fontSize: 12.5, color: '#6b7280', mt: 0.25 }}>{description}</Typography>
      ) : null}
    </Box>
    <Switch checked={value} disabled={disabled} onChange={(e) => onToggle(e.target.checked)} />
    {onDelete ? (
      <Tooltip title='Delete flag'>
        <span>
          <IconButton size='small' color='error' disabled={disabled} onClick={onDelete}>
            <FontAwesomeIcon icon={['fas', 'trash-alt']} />
          </IconButton>
        </span>
      </Tooltip>
    ) : null}
  </Box>
)

const SectionLabel = ({ children, sx }) => (
  <Typography
    sx={{
      fontSize: 11,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: '#98a2b3',
      fontWeight: 700,
      mb: 0.5,
      ...sx
    }}
  >
    {children}
  </Typography>
)

const KEY_RE = /^[A-Za-z][A-Za-z0-9_]*$/

// Create a custom flag: key (validated) + initial value.
const CreateFlagDialog = ({ open, onClose, onCreate, exists }) => {
  const [key, setKey] = useState('')
  const [description, setDescription] = useState('')
  const [value, setValue] = useState(false)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  const reset = () => {
    setKey('')
    setDescription('')
    setValue(false)
    setError(null)
  }

  const close = () => {
    if (saving) return
    reset()
    onClose()
  }

  const submit = async () => {
    const k = key.trim()
    if (!k) return setError('Enter a flag key.')
    if (!KEY_RE.test(k))
      return setError('Key must start with a letter and use only letters, numbers or underscore.')
    if (exists(k)) return setError('A flag with that key already exists.')
    setSaving(true)
    const res = await onCreate(k, value, description.trim())
    setSaving(false)
    if (!res?.error) {
      reset()
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth='xs' fullWidth>
      <DialogTitle>Create custom flag</DialogTitle>
      <DialogContent dividers>
        <Typography variant='caption' color='textSecondary'>
          Use a key your app code reads (e.g. showXyzWidget). A flag with no code reading it has no effect.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size='small'
          label='Flag key'
          value={key}
          onChange={(e) => {
            setKey(e.target.value)
            setError(null)
          }}
          error={!!error}
          helperText={error || ''}
          sx={{ mt: 2 }}
        />
        <TextField
          fullWidth
          size='small'
          label='Description (optional)'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
        <FormControlLabel
          control={<Switch checked={value} onChange={(e) => setValue(e.target.checked)} />}
          label={value ? 'Enabled' : 'Disabled'}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={saving}>
          Cancel
        </Button>
        <Button variant='contained' onClick={submit} disabled={saving}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const FeatureFlags = () => {
  const { known, custom, setFlag, removeFlag, exists, busy } = useFeatureFlags()
  const [openCreate, setOpenCreate] = useState(false)

  return (
    <>
      <Box className='bg-primary text-primary-foreground px-4 py-1 flex gap-4 items-center justify-between sticky top-0 z-10 h-8'>
        <Typography variant='subtitle1' className='uppercase'>
          Feature Flags
        </Typography>
        <ButtonStartIcon
          onClick={() => setOpenCreate(true)}
          color='secondary'
          variant='contained'
          startIcon={['fas', 'plus']}
        >
          Create
        </ButtonStartIcon>
      </Box>

      <Box sx={{ px: 2, py: 2 }}>
        <SectionLabel>Available flags</SectionLabel>
        {known.map((f) => (
          <FlagRow
            key={f.key}
            title={f.label}
            description={f.description}
            value={f.value}
            disabled={busy === f.key}
            onToggle={(v) => setFlag(f.key, v)}
          />
        ))}

        <SectionLabel sx={{ mt: 3 }}>Custom flags</SectionLabel>
        {custom.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: '#98a2b3', py: 1 }}>
            No custom flags. Use “Create” to add one (it needs matching code to take effect).
          </Typography>
        ) : (
          custom.map((f) => (
            <FlagRow
              key={f.key}
              title={f.key}
              description={f.description}
              value={f.value}
              disabled={busy === f.key}
              onToggle={(v) => setFlag(f.key, v)}
              onDelete={() => removeFlag(f.key)}
            />
          ))
        )}
      </Box>

      <CreateFlagDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreate={setFlag}
        exists={exists}
      />
    </>
  )
}

export default FeatureFlags
